<template>
  <!-- Primary level nav item -->
  <a
    v-if="!firstLevelItem.children.length"
    class="c-nav-desktop__primary-link"
    :href="firstLevelItem.url"
    :ref="(el) => addFocusableRef(el)"
  >
    {{ firstLevelItem.label }}
  </a>
  <button
    v-else
    class="c-nav-desktop__primary-link"
    :aria-controls="`second-level-list-${firstLevelNumber}`"
    :aria-expanded="panelIsOpen"
    @click="() => emit('primaryLevelButtonClick', firstLevelNumber)"
    :ref="(el) => addFocusableRef(el)"
  >
    {{ firstLevelItem.label }}
    <svg focusable="false" aria-hidden="true">
      <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#down-arrow"></use>
    </svg>
  </button>

  <!-- Meganav panel -->
  <section
    class="c-nav-desktop__panel"
    :aria-label="firstLevelItem.label"
    tabindex="-1"
    :hidden="panelHidden"
    :inert="panelInert"
    v-if="firstLevelItem.children.length"
  >
    <ul class="c-nav-desktop__panel-list" :id="`second-level-list-${firstLevelNumber}`" role="list">
      <!-- Link to the parent primary nav item's page -->
      <li
        v-if="firstLevelItem.url"
        class="c-nav-desktop__item c-nav-desktop__item--parent"
        role="listitem"
      >
        <a
          class="c-nav-desktop__link c-nav-desktop__link--second-level"
          :href="firstLevelItem.url"
          :ref="(el) => addFocusableRef(el)"
        >
          {{ firstLevelItem.label }}
        </a>
      </li>
      <!-- Second level: Child pages of primary items -->
      <li
        class="c-nav-desktop__item c-nav-desktop__item--second-level"
        v-for="(secondLevelItem, secondLevelNumber) in firstLevelItem.children"
        :key="`second-level-${secondLevelNumber}`"
        role="listitem"
      >
        <a
          class="c-nav-desktop__link c-nav-desktop__link--second-level"
          :href="secondLevelItem.url"
          :ref="(el) => addFocusableRef(el)"
        >
          {{ secondLevelItem.label }}
        </a>
        <!-- Third level: Child pages of secondary items -->
        <template v-if="secondLevelItem.children.length">
          <ul
            class="c-nav-desktop__third-level"
            :id="`third-level-list-${firstLevelNumber}-${secondLevelNumber}`"
            role="list"
          >
            <li
              class="c-nav-desktop__item c-nav-desktop__item--third-level"
              v-for="(thirdLevelItem, thirdLevelNumber) in secondLevelItem.children"
              :key="`third-level-${thirdLevelNumber}`"
              role="listitem"
            >
              <a
                class="c-nav-desktop__link c-nav-desktop__link--third-level"
                :href="thirdLevelItem.url"
                :ref="(el) => addFocusableRef(el)"
              >
                {{ thirdLevelItem.label }}
              </a>
            </li>
          </ul>
        </template>
      </li>
    </ul>
  </section>
</template>

<script setup>
import { inject, computed, onBeforeUpdate, onMounted, ref } from 'vue';
const config = inject('config');

const props = defineProps({
  activeFirstLevelNumber: {
    type: [Number, null],
    required: true,
  },
  firstLevelItem: {
    type: Object,
    required: true,
  },
  firstLevelNumber: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['primaryLevelButtonClick', 'closeMeganav']);

const focusableElementRef = ref([]);

const panelIsOpen = computed(() => props.firstLevelNumber === props.activeFirstLevelNumber);

const panelHidden = computed(() => (panelIsOpen ? null : 'hidden'));
const panelInert = computed(() => (panelIsOpen ? null : 'true'));

function addFocusableRef(el) {
  focusableElementRef.value.push(el);
}

function handleTabbing() {
  const firstFocucableEl = focusableElementRef.value[0];
  const lastFocusableEl = focusableElementRef.value[focusableElementRef.value.length - 1];

  if (lastFocusableEl) {
    lastFocusableEl.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        emit('closeMeganav');
      }
    });
  }

  if (firstFocucableEl) {
    firstFocucableEl.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && e.shiftKey) {
        emit('closeMeganav');
      }
    });
  }
}

onBeforeUpdate(() => {
  focusableElementRef.value = [];
});

onMounted(() => {
  handleTabbing();
});
</script>
